<template>
  <div class="explicacion-ia">
    <div class="botonera">
      <f7-button small fill icon-f7="wand_stars" :text="$t('explicacionia.explicarAprobar')" class="display-flex" @click="pedir('APROBAR')"></f7-button>
      <f7-button small icon-f7="wand_stars" :text="$t('explicacionia.explicarDenegar')" class="display-flex margin-left-half" @click="pedir('DENEGAR')"></f7-button>
    </div>

    <div v-if="resultado" class="resultado">
      <!-- [JJCS, 01/10/2026] Las dos mitades van separadas y etiquetadas. La primera la escribe
           el motor determinista y es la que trae el color, los días y las cifras; la segunda es
           la prosa del asistente, que no conoce ninguno de esos datos. Juntarlas en un solo
           párrafo con un pie que decía «Redactado por la IA» atribuía al asistente también lo
           que había calculado el sistema. -->
      <p class="texto">{{ resultado.explicacionPlantilla || resultado.explicacion }}</p>
      <template v-if="resultado.prosaIaExplicacion">
        <p class="texto prosa-ia">{{ resultado.prosaIaExplicacion }}</p>
        <p class="fuente-linea">{{ $t('explicacionia.prosaDelAsistente', { modelo: resultado.modelo || '—' }) }}</p>
      </template>

      <!-- El borrador se enseña siempre editable y nunca se envía solo: lo confirma la
           persona. Si la IA no ha contestado, esto trae el texto de plantilla con su hueco,
           que sirve igual de punto de partida. -->
      <div class="borrador">
        <div class="borrador-titulo">
          {{ resultado.accion == 'DENEGAR' ? $t('explicacionia.borradorDenegar') : $t('explicacionia.borradorAprobar') }}
        </div>
        <textarea v-model="borrador" class="borrador-texto" rows="4"></textarea>
        <div class="acciones">
          <f7-button small fill :text="$t('explicacionia.usarMotivo')" @click="confirmar"></f7-button>
          <span class="fuente">{{ pieBorrador }}</span>
        </div>
      </div>

      <p v-for="aviso in resultado.avisos" :key="aviso" class="aviso">
        {{ textoCodigo('explicacionia.avisos', aviso) }}
      </p>
    </div>
  </div>
</template>
<style scoped>
.explicacion-ia { margin-top: 8px; font-size: 13px; }
.explicacion-ia .resultado { margin-top: 8px; }
.explicacion-ia .texto { margin: 0 0 6px; }
/* La prosa del asistente se ve como lo que es: un añadido al texto del sistema, no el texto. */
.explicacion-ia .prosa-ia {
  border-left: 3px solid #d5d8dc;
  padding-left: 6px;
  font-style: italic;
}
.explicacion-ia .fuente-linea { color: #8e8e93; font-size: 12px; margin: 0 0 6px; }
.borrador { background-color: #f4f5f7; border-radius: 4px; padding: 6px 8px; }
.borrador-titulo { font-weight: bold; margin-bottom: 4px; }
.borrador-texto {
  width: 100%;
  border: 1px solid #d5d8dc;
  border-radius: 4px;
  padding: 4px 6px;
  font-family: inherit;
  font-size: 13px;
  background-color: #fff;
}
.acciones { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
.acciones .fuente { color: #8e8e93; font-size: 12px; }
.aviso { color: #b26a00; margin: 6px 0 0; }
</style>
<script>
import RhCoincidencias from "./../js/db/RhCoincidencias.js";
import textoCodigo from "./../js/utils/textoCodigo.js";
import preloaderDeLaSesion from "./../js/utils/preloaderDeLaSesion.js";

/**
 * Explicación del semáforo y borrador de motivo **para el aprobador** (#65).
 *
 * No es para el solicitante: a él la explicación le llega ya dentro de `evaluar`, de plantilla
 * y sin nombres. Aquí se pide al servicio de IA que lo ponga en palabras y redacte un motivo
 * para aprobar o para denegar.
 *
 * Dos cosas que este componente no hace, a propósito:
 * - **No cambia el color.** El semáforo lo sigue pintando `rSemaforoCoincidencias` con lo que
 *   dice el motor; aquí solo se enseña texto.
 * - **No envía el motivo.** Lo emite por `motivo` cuando la persona pulsa «Usar este motivo»,
 *   después de poder editarlo. Quien lo use es quien aprueba o deniega.
 *
 * Si el servicio de IA está caído, la respuesta llega igual toda de plantilla: se enseña el texto
 * de respaldo —con el hueco del motivo sin rellenar— y se dice de dónde sale.
 *
 * [JJCS, 01/10/2026] Y la fuente se enseña **por pieza**, no del conjunto: el color, los días y
 * las cifras los escribe siempre el motor y el asistente solo aporta prosa, así que no hay un
 * texto que sea «de la IA» entero.
 */
export default {
  name: 'r-explicacion-ia',
  props: {
    /** Solicitud a explicar: { username, fechaInicio, fechaFin, solicitudId }. */
    solicitud: {
      type: Object,
      default: null
    }
  },
  data: function() {
    return {
      resultado: null,
      borrador: '',
      // Turno de la última petición pedida. Sirve para descartar la respuesta de una anterior
      // que llegue tarde: si no, sobrescribiría la explicación del semáforo que se está viendo.
      turno: 0
    };
  },
  watch: {
    // El componente no se desmonta entre comprobaciones —sigue habiendo evaluación—, así que
    // el texto de la solicitud anterior se quedaría en pantalla junto al semáforo nuevo.
    solicitud: function() {
      this.limpiar();
    }
  },
  computed: {
    /**
     * El pie del borrador, y mira SOLO al borrador.
     *
     * [JJCS, 01/10/2026] El asistente puede aportar prosa a la explicación y no al borrador, o al
     * contrario. Con un único `fuente` del conjunto, un borrador escrito entero por la plantilla
     * se presentaba como «Redactado por la IA» en cuanto la explicación llevara prosa suya.
     */
    pieBorrador: function() {
      if (!this.resultado) return '';
      if (this.resultado.fuenteBorrador != 'MIXTA') return this.$t('explicacionia.borradorPlantilla');
      return this.$t('explicacionia.borradorConProsaIa', { modelo: this.resultado.modelo || '—' });
    }
  },
  methods: {
    textoCodigo: function(espacio, codigo) {
      return textoCodigo(this, espacio, codigo);
    },
    /** Borra lo que se estaba enseñando e invalida lo que estuviera en vuelo. */
    limpiar: function() {
      this.resultado = null;
      this.borrador = '';
      this.turno++;
    },
    pedir: function(accion) {
      const self = this;
      const app = self.$f7;
      if (!self.solicitud || !self.solicitud.username || !self.solicitud.fechaInicio
        || !self.solicitud.fechaFin) {
        app.dialog.alert(self.$t('explicacionia.faltanDatos'));
        return;
      }
      // Se borra lo que hubiera antes de pedir lo nuevo: si ya había un borrador para aprobar y
      // ahora se pide el de denegar, dejarlo en pantalla permite copiar un motivo de la decisión
      // contraria a la que se acaba de pulsar, y si la petición falla se queda ahí para siempre.
      self.limpiar();
      const turno = self.turno;
      // [revisión #1711] Cada petición suelta su propia espera, no la de otra: una respuesta a
      // destiempo no pinta nada, pero sí tiene que devolver lo que pidió sin quitarle el
      // preloader a la que sigue en vuelo. #663 · Y atada a la sesión: si cambia en vuelo, la
      // frontera calla el `then` que la soltaba, y un contador propio se quedaba puesto para siempre.
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      RhCoincidencias.explicar({
        username: self.solicitud.username,
        fechaInicio: self.solicitud.fechaInicio,
        fechaFin: self.solicitud.fechaFin,
        solicitudId: self.solicitud.solicitudId || null,
        accion: accion
      })
        .then(function(res) {
          if (turno != self.turno) return;
          self.resultado = res || null;
          self.borrador = (res && res.borradorMotivo) || '';
        })
        .catch(function(e) {
          if (turno != self.turno) return;
          app.dialog.alert(e && e.message ? e.message : self.$t('explicacionia.error'));
        })
        .then(function() {
          espera.cerrar();
        });
    },
    confirmar: function() {
      if (!this.borrador || !this.borrador.trim()) {
        this.$f7.dialog.alert(this.$t('explicacionia.motivoVacio'));
        return;
      }
      this.$emit('motivo', {
        accion: this.resultado ? this.resultado.accion : null,
        motivo: this.borrador.trim()
      });
    }
  }
};
</script>
